import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import axiosRetry from 'axios-retry'
import qs, { IStringifyOptions } from 'qs'

import yamadumiAuth from '../Auth'
import Env from '../Env'

interface Result<T> {
  error: boolean
  messages: Array<{ id: string, message: string, targets: string[] }>
  data: T
}

interface YamadumiApiRequestConfig extends AxiosRequestConfig {
  /**
   * @param indices
   * qs.stringify({ a: ['b', 'c'] }, { arrayFormat: 'indices' })
   * 'a[0]=b&a[1]=c'
   * @param brackets
   * qs.stringify({ a: ['b', 'c'] }, { arrayFormat: 'brackets' })
   * 'a[]=b&a[]=c'
   * @param repeat
   * qs.stringify({ a: ['b', 'c'] }, { arrayFormat: 'repeat' })
   * 'a=b&a=c'
   * @param comma
   * qs.stringify({ a: ['b', 'c'] }, { arrayFormat: 'comma' })
   * 'a=b,c'
   */
  arrayFormat?: IStringifyOptions['arrayFormat']
  disableCommonErrorHandling?: boolean
}

interface YamadumiApi extends AxiosInstance {
  get: <T = any, R = Result<T>>(
    url: string,
    params?: Record<string, unknown>,
    config?: YamadumiApiRequestConfig
  ) => Promise<R>

  post: <T = any, R = Result<T>>(
    url: string,
    data?: any,
    config?: YamadumiApiRequestConfig
  ) => Promise<R>

  delete: <T = any, R = Result<T>>(
    url: string,
    params?: Record<string, unknown>,
    config?: YamadumiApiRequestConfig
  ) => Promise<R>

  put: <T = any, R = Result<T>>(
    url: string,
    data?: any,
    config?: YamadumiApiRequestConfig
  ) => Promise<R>

  all?: <T>(values: Array<T | Promise<T>>) => Promise<T[]>
}

/**
 * request interceptor
 */
const interceptRequest = (instance: AxiosInstance) => {
  instance.interceptors.request.use(async (config) => {
    if (yamadumiAuth.hasToken()) {
      // アクセストークンの有効期限が切れているなら、
      // リクエストを送る前にトークンをリフレッシュする
      if (!yamadumiAuth.authenticated()) {
        try {
          await yamadumiAuth.refreshAuthenticate()
        } catch {
          window.location.assign('/')
        }
      }

      // 認証情報をリクエストヘッダに埋め込む
      // API側の認証では、ヘッダにある access-token を見て
      // トークンイントロスぺクションエンドポイントにより認証している。
      if (yamadumiAuth.accessToken != null) {
        if (config.headers == null) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          config.headers = {} as AxiosRequestHeaders
        }
        config.headers['access-token'] = yamadumiAuth.accessToken
      }
    }
    return config
  })
}

/**
 * response interceptor
*/
// ここでuseNavigateが使えないので、一度履歴に飛び先をpushし、
// ブラウザバックしてからもう一度forwardすることで
// 無理やりReactに画面変更を認識させる。
const interceptResponse = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => response.data,
    (error: AxiosError) => {
      const yamadumiRequestConfig = error.config as YamadumiApiRequestConfig
      if (yamadumiRequestConfig.disableCommonErrorHandling === true) {
        throw error
      }

      window.history.pushState(null, '', '/request-error')
      window.history.back()
      window.addEventListener('popstate', () =>
        window.history.forward(), { once: true })
    }
  )
}

/**
 *  convert request
 */
const setRequestProxy = (instance: AxiosInstance) => {
  const _get = instance.get
  const _delete = instance.delete

  const appendParamsToUrl = (
    url: string,
    params?: Record<string, unknown>,
    arrayFormat?: YamadumiApiRequestConfig['arrayFormat']
  ) => {
    return `${url}${qs.stringify(params, {
      addQueryPrefix: true,
      arrayFormat: arrayFormat ?? 'repeat'
    })}`
  }

  instance.get = async (
    url: string,
    params?: Record<string, unknown>,
    config?: YamadumiApiRequestConfig
  ) => {
    let axiosRequestConfig: AxiosRequestConfig | undefined

    if (config !== undefined) {
      const { arrayFormat, ...restConfig } = config
      axiosRequestConfig = restConfig
    }

    return await _get(
      appendParamsToUrl(url, params, config?.arrayFormat),
      axiosRequestConfig
    )
  }

  instance.delete = async (
    url: string,
    params?: Record<string, unknown>,
    config?: YamadumiApiRequestConfig
  ) => {
    let axiosRequestConfig: AxiosRequestConfig | undefined

    if (config !== undefined) {
      const { arrayFormat, ...restConfig } = config
      axiosRequestConfig = restConfig
    }

    return await _delete(
      appendParamsToUrl(url, params, config?.arrayFormat),
      axiosRequestConfig
    )
  }
}

const createYamadumiApi = () => {
  const _apiTimeout = Env.WEBAPI_TIMEOUT_MS
  const apiTimeout = isNaN(_apiTimeout) ? 5000 : _apiTimeout

  const yamadumiApi = axios.create({
    baseURL: Env.WEBAPI_URI,
    timeout: apiTimeout,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  axiosRetry(yamadumiApi, {
    retries: Env.WEBAPI_RETRY,
    retryDelay: axiosRetry.exponentialDelay,
    onRetry: (retryCount, error, requetsConfig) => {
      console.log(
        `山積APIの実行に失敗しました。リトライします… ${retryCount}回目`
      )
    }
  })

  interceptRequest(yamadumiApi)
  interceptResponse(yamadumiApi)
  setRequestProxy(yamadumiApi)

  return yamadumiApi as YamadumiApi
}

export const yamadumiApi = createYamadumiApi()
